import InfoIcon from 'assets/icons/info.svg';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import useIsClient from 'hooks/common/use-is-client';
import useMobile from 'hooks/common/use-mobile';
import { StaticPDPProduct } from 'modules/product-details/types';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import style from './judge-me-reviews.module.css';
import useJudgeMeReviewsTracking from './use-judge-me-reviews-tracking';
import debounce from 'lodash/debounce';

const Sheet = dynamic(() => import('components/sheet'), {
  ssr: false,
});

const Tooltip = dynamic(() => import('components/tooltip'), {
  ssr: false,
});

interface Props {
  product: StaticPDPProduct;
}

/**
 * Change review count style (replaces link with span where number is bold)
 */
const changeReviewCountStyle = debounce(
  (rootElement: HTMLDivElement | null) => {
    if (!rootElement) return;
    const targetElement: Element | null = rootElement?.querySelector(
      '.jdgm-rev-widg__summary-text'
    );

    // if there is no element or styles already applied – do nothing
    if (!targetElement || targetElement.querySelector('span.font-black'))
      return;

    if (targetElement.textContent) {
      const text = targetElement.textContent;
      const parts = text.split(' ');

      parts[0] = `<span class="font-black text-lg">${parts[0]}</span>`;

      targetElement.innerHTML = parts.join(' ');
    }
  },
  200
);

/**
 * JudgeMeReviews component.
 * It's rendered in the Product Details page using the JudgeMe API.
 */
const JudgeMeReviews: React.FC<Props> = ({ product }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isClient } = useIsClient();
  const isMobile = useMobile();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  useJudgeMeReviewsTracking(widgetRef);

  const handleInfoClick = (): void => {
    if (isMobile) {
      setIsOpen(true);
    } else {
      setShowTooltip(!showTooltip);
    }
  };

  // this hack is needed to apply our own styles since we do not have control on JudgeMe render
  useEffect(() => {
    if (!widgetRef.current || !isClient) return;

    // observes the widget container and calls changeReviewCountStyle every mutation
    const observer = new MutationObserver(() =>
      changeReviewCountStyle(widgetRef.current)
    );

    observer.observe(widgetRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, [isClient]);

  if (!isClient) return null;

  return (
    <>
      <ImpressionTrackerNew element="PDP Reviews">
        <div
          className={`container ${style['reviews-container']}`}
          data-test="reviews-container"
        >
          <div className="-mb-2 flex items-center justify-between lg:justify-start lg:gap-2">
            <p className="text-text-light-bg-primary font-black">
              <FormattedMessage id="pdp:judge-me-reviews:tooltip:title" />
            </p>
            <button
              ref={infoButtonRef}
              onClick={handleInfoClick}
              data-test="reviews-info-button"
              aria-label="Review verification info"
              aria-describedby={showTooltip ? 'tooltip-content' : undefined}
            >
              <InfoIcon className="text-action-active h-6 w-6" />
            </button>
          </div>
          <div
            className="jdgm-widget jdgm-review-widget jdgm-outside-widget text-grey-700 font-sans"
            data-id={product.id}
            data-product-title={product.title}
            ref={widgetRef}
          />
        </div>
      </ImpressionTrackerNew>

      {/* Desktop Tooltip */}
      {!isMobile && (
        <Tooltip
          isOpen={showTooltip}
          targetRef={infoButtonRef}
          onClose={() => setShowTooltip(false)}
        >
          <p
            className="text-text-light-bg-primary text-center text-sm"
            data-test="reviews-tooltip-description"
          >
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:description" />
          </p>
        </Tooltip>
      )}

      {/* Mobile Sheet */}
      <Sheet isOpen={isOpen && isMobile} onClose={() => setIsOpen(false)}>
        <div className="px-4 pb-8">
          <p className="text-text-light-bg-primary mb-2 text-center font-black">
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:title" />
          </p>
          <p className="text-text-light-bg-secondary text-sm leading-tight">
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:description" />
          </p>
        </div>
      </Sheet>
    </>
  );
};

export default JudgeMeReviews;
